/**
 * dog-documents.js
 */

import React from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { Box, Typography, Link } from '@mui/material';
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import { makeLocation } from 'utils/routing';
import { objectToFormData } from "utils/form-data";
import Button from 'components/button';
import CategoryDocsEditor from 'components/category-docs-editor';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerContent from 'components/menu-drawer/drawer-content';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DogDrawerMobileHeader from './dog-drawer-mobile-header';
import { CategoryTitle, FileList } from 'components/category-docs-editor/category';
import Icon from 'components/icon';


const DogDocuments = (props) => {
  const {
    drawerState,
    dog,
    onSave,
    onClose,
    basePath
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [docFields, setDocFields] = React.useState();
  const [isDirty, setIsDirty] = React.useState(false);
  const title = 'Uploaded Documents';
  const vetDocsTitle = 'Veterinary Docs';


  const onDocFieldsChanged = (docFields) => {
    setDocFields(docFields);
    setIsDirty(true);
  }


  const saveDocuments = async () => {
    try {
      drawerState.loading.content.set(true);

      let formData = objectToFormData(
        {
          documents: docFields.documents,
          fileDeletions: docFields.deletions.files,
          categoryDeletions: docFields.deletions.categories
        },
        '',
        ['category', 'url', 'localId']
      );

      const response = await axios.put(`dogs/${dog.id}/documents`, formData);

      if (response.isSuccess) {
        const docs = response.data;
        setIsDirty(false);
        onSave({ ...dog, documents: docs });
        enqueueSnackbar('Dog documents saved', { variant: 'success' });
      }
    } finally {
      drawerState.loading.content.set(false);
    }
  }


  const cancelButton = (
    <Button upperCase={false} onClick={onClose}>Cancel</Button>
  );


  const saveButton = (
    <DrawerActionButton
      level={1}
      disabled={!isDirty}
      style={{ marginLeft: 16 }}
      onClick={saveDocuments}
      data-testid='save-dog-button'
    >
      Save
    </DrawerActionButton>
  );


  const mobileAppBar = (
    <DogDrawerMobileHeader
      title={title}
      onClose={props.onClose}
      saveButton={saveButton}
      basePath={basePath}
    />
  );


  return (
    <>
      <DrawerContentBuilder
        mobileAppBar={{ content: mobileAppBar }}
        desktopHeader={{ title, actions: <>{cancelButton}{saveButton}</> }}
        stickyHeader
        children={(
          <DrawerContent>
            <CategoryDocsEditor
              documents={dog?.documents}
              setDocFields={onDocFieldsChanged}
            />

            {/* vet visit documents */}
            {dog?.vetDocuments && dog.vetDocuments.length &&
              <Box sx={{ margin: '28px 4px 0' }}>
                <Box sx={{ marginLeft: `5px`, marginBottom: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CategoryTitle title={vetDocsTitle} />
                    <Icon type='lock' fontSize='small' sx={{ marginLeft: 1 }} />
                  </Box>
                  <Typography variant='subtext-large'>
                    (Auto-imported from your records)
                  </Typography>
                </Box>
                <FileList
                  files={dog.vetDocuments.map(d => ({
                    ...d, 
                    localId: d.id, 
                    subtitle: <>
                      <Link
                        component={RouterLink}
                        onClick={(e) => e.stopPropagation()}
                        to={makeLocation(
                          '/records',
                          'vetVisit',
                          'view',
                          { vetVisitId: d.vetVisitId }
                        )}
                      >
                        {d.vetVisitName}
                      </Link>
                      {' \u2022 '}
                      {format(d.vetVisitDate, 'MM/dd/yyyy')}
                    </>
                  }))}
                  categoryName={vetDocsTitle}
                />
              </Box>
            }
          </DrawerContent>
        )}
      />

      <ConfirmUnsavedEditor dirty={isDirty} save={saveDocuments} />
    </>
  );
}

export default DogDocuments;